/**
 * CSV Export Utility
 * Converts an array of objects to a downloadable CSV file.
 */

/**
 * Convert a 2D array (rows of values) to CSV string.
 */
function rowsToCsv(rows: (string | number | null | undefined)[][]): string {
  return rows
    .map((row) =>
      row
        .map((cell) => {
          const str = cell == null ? '' : String(cell);
          // Escape double quotes and wrap in quotes if needed
          if (str.includes(',') || str.includes('"') || str.includes('\n')) {
            return `"${str.replace(/"/g, '""')}"`;
          }
          return str;
        })
        .join(',')
    )
    .join('\n');
}

/**
 * Download a CSV file in the browser.
 */
function downloadCsv(csvContent: string, filename: string): void {
  const bom = '\uFEFF';
  const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Export an array of objects to a CSV download.
 * @param data — array of records to export
 * @param filename — download filename (without extension is fine)
 * @param columns — optional: explicit column ordering. If omitted, keys from the first row are used.
 */
export function exportToCsv<T extends Record<string, unknown>>(
  data: T[],
  filename: string,
  columns?: { key: keyof T; label: string }[]
): void {
  if (data.length === 0) {
    alert('No data to export.');
    return;
  }

  const cols = columns ?? Object.keys(data[0]).map((k) => ({ key: k as keyof T, label: k }));
  const headers = cols.map((c) => c.label);
  const rows = data.map((item) => cols.map((c) => item[c.key] as string | number | null | undefined));

  const csv = rowsToCsv([headers, ...rows]);
  downloadCsv(csv, filename);
}

/**
 * Format a date string as YYYY-MM-DD for CSV filenames.
 */
export function csvFilenameDate(): string {
  return new Date().toISOString().slice(0, 10);
}
